import useMediaQuery from '@mui/material/useMediaQuery';

type borderRadiusType = 3 | 4 | 6 | 8 | 12 | 16 | 24 | 28 | 40 | 48;
export function borderRadius(size: borderRadiusType = 6) {
  return `${size}px`;
}

type DeviceSizeType = 'xl' | 'desktop' | 'tablet' | 'smartphone' | 'xs';
type DeviceTypeMap = {
  [key in DeviceSizeType]: number;
};
export const deviceSizes: DeviceTypeMap = {
  xl: 1440,
  desktop: 1080,
  tablet: 768,
  smartphone: 414,
  xs: 320,
};

export type ZIndexSizeType =
  | 100 // child
  | 200 // fav
  | 300 // button
  | 400 // baloon, tooltip, banner
  | 500 // nav
  | 800 // notification
  | 1100 // header
  | 1200 // drawer
  | 1300 // modal
  | 1400 // menu
  | 1500; // meeting widget / page modal
export function zIndex(size: ZIndexSizeType) {
  return size;
}

export function clearfix() {
  return `
    &:after {
      display: table;
      content: '';
      clear: both;
    }
  `;
}

type SpacingSizeType =
  | 2 // tiny
  | 3
  | 4
  | 6 // small
  | 8
  | 12
  | 14
  | 16 // medium
  | 20
  | 24
  | 28
  | 32
  | 36
  | 40 // large
  | 48
  | 56 // huge
  | 64
  | 72
  | 80
  | 88
  | 96
  | 102
  | 104
  | 120
  | 128
  | 140
  | 160;
export function spacing(size: SpacingSizeType) {
  return `${size}px`;
}

type FontSizeType =
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 18
  | 20
  | 22
  | 24
  | 26
  | 28
  | 32
  | 36
  | 40
  | 42
  | 44
  | 48
  | 56;
export function fontSize(size: FontSizeType) {
  return `${size}px`;
}

type FontWeightType = 'normal' | 'bold';
export function fontWeight(size: FontWeightType) {
  if (size === 'bold') {
    return '600';
  }
  return size;
}

type MediaQueryMap = {
  [key in DeviceSizeType]: string;
};
export const mediaMax: MediaQueryMap = {
  xl: `media (max-width: ${deviceSizes.xl}px)`,
  desktop: `@media (max-width: ${deviceSizes.desktop}px)`,
  smartphone: `@media (max-width: ${deviceSizes.smartphone}px)`,
  tablet: `@media (max-width: ${deviceSizes.tablet}px)`,
  xs: `@media (max-width: ${deviceSizes.xs}px)`,
};
export const mediaMin: MediaQueryMap = {
  xl: `@media (min-width: ${deviceSizes.xl - 1}px)`,
  desktop: `@media (min-width: ${deviceSizes.desktop - 1}px)`,
  smartphone: `@media (min-width: ${deviceSizes.smartphone - 1}px)`,
  tablet: `@media (min-width: ${deviceSizes.tablet - 1}px)`,
  xs: `@media (min-width: ${deviceSizes.xs - 1}px)`,
};

export const isMediaMin = (size: DeviceSizeType) => {
  return useMediaQuery(`(min-width: ${deviceSizes[size] - 1}px)`);
};
export const isMediaMax = (size: DeviceSizeType) => {
  return isMediaMaxWidth(deviceSizes[size]);
};

export const isMediaMaxWidth = (size: number) => {
  return useMediaQuery(`(max-width: ${size}px)`);
};

type ShadowSizeType = 1 | 2 | 3 | 4;
export function shadow(size: ShadowSizeType) {
  return `
    0 ${size}px ${size * 3}px rgba(0, 0, 0, 0.15)
  `;
}

// レイアウトを強制的に変える場合のテーマ(ThemeProviderと使う)
type LayoutTheme = {
  mediaMax: MediaQueryMap;
  mediaMin: MediaQueryMap;
};
export const defaultTheme: LayoutTheme = {
  mediaMax: { ...mediaMax },
  mediaMin: { ...mediaMin },
};
const mediaAll = '@media all';
export const forceLayoutTheme: LayoutTheme = {
  mediaMax: {
    xl: mediaAll,
    desktop: mediaAll,
    smartphone: mediaAll,
    tablet: mediaAll,
    xs: mediaAll,
  },
  mediaMin: {
    xl: mediaAll,
    desktop: mediaAll,
    smartphone: mediaAll,
    tablet: mediaAll,
    xs: mediaAll,
  },
};
export const mediaMaxWithLayoutTheme = (
  props: { theme: LayoutTheme | any }, // TODO
  key: DeviceSizeType,
) =>
  props.theme &&
  props.theme.mediaMax &&
  Object.keys(props.theme.mediaMax).includes(key)
    ? props.theme.mediaMax[key]
    : mediaMax[key];
export const mediaMinWithLayoutTheme = (
  props: { theme: LayoutTheme | any }, // TODO
  key: DeviceSizeType,
) =>
  props.theme &&
  props.theme.mediaMin &&
  Object.keys(props.theme.mediaMin).includes(key)
    ? props.theme.mediaMin[key]
    : mediaMin[key];

export const hideScrollbar = `
  &::-webkit-scrollbar {
    display: none;
  }
  &::-ms-overflow-style: none {
    display: none;
  }
  scrollbar-width: none;
`;
